<template>
  <div class="overview-section">
    <h3 class="section-title">
      <i class="fas fa-chart-pie"></i> Visão Geral
    </h3>
    
    <div class="metrics-grid">
      <div class="metric-card current-rating">
        <div class="metric-icon">
          <i class="fas fa-star"></i>
        </div>
        <div class="metric-info">
          <span class="metric-label">Avaliação Atual</span>
          <div class="metric-value">{{ getCurrentRating() }}<span class="out-of">/5</span></div>
        </div>
      </div>
      
      <div class="metric-card monthly-variation" :class="getMonthlyVariationClass()">
        <div class="metric-icon">
          <i class="fas" :class="getMonthlyVariationIcon()"></i>
        </div>
        <div class="metric-info">
          <span class="metric-label">Variação Mensal</span>
          <div class="metric-value">{{ getMonthlyVariation() }}</div>
        </div>
      </div>
      
      <div class="metric-card" :class="getStatusClass()">
        <div class="metric-icon">
          <i class="fas" :class="getStatusIcon()"></i>
        </div>
        <div class="metric-info">
          <span class="metric-label">Status</span>
          <div class="metric-value">{{ getStatusText() }}</div>
        </div>
      </div>
      
      <div class="metric-card establishments">
        <div class="metric-icon">
          <i class="fas fa-building"></i>
        </div>
        <div class="metric-info">
          <span class="metric-label">Estabelecimentos</span>
          <div class="metric-value">{{ getEstablishmentsCount() }}</div>
        </div>
      </div>
    </div>
    
    <div class="summary-box">
      <h4 class="summary-title">
        <i class="fas fa-lightbulb"></i> Resumo Situacional
      </h4>
      <p class="summary-text">{{ getSituationalSummary() }}</p>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    categoryData: Object,
    categoryName: String,
    month: Number,
    year: Number,
    monthName: String
  },
  methods: {
    getCurrentRating() {
      if (!this.categoryData || !this.categoryData.average_data || 
          !this.categoryData.average_data.average_monthly_ratings || 
          !this.categoryData.average_data.average_monthly_ratings[this.year]) {
        return '0.0'
      }
      
      const monthlyRating = this.categoryData.average_data.average_monthly_ratings[this.year][this.month]
      return monthlyRating ? monthlyRating.toFixed(1) : '0.0'
    },
    
    getMonthlyVariation() {
      if (!this.categoryData || !this.categoryData.average_data || 
          !this.categoryData.average_data.average_monthly_ratings || 
          !this.categoryData.average_data.average_monthly_ratings[this.year]) {
        return '0%'
      }
      
      const currentMonth = this.month
      const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1
      const previousYear = currentMonth === 1 ? this.year - 1 : this.year
      
      const currentRating = this.categoryData.average_data.average_monthly_ratings[this.year][currentMonth] || 0
      
      let previousRating = 0
      if (this.categoryData.average_data.average_monthly_ratings[previousYear]) {
        previousRating = this.categoryData.average_data.average_monthly_ratings[previousYear][previousMonth] || 0
      }
      
      if (previousRating === 0) return 'N/A'
      
      const variation = ((currentRating - previousRating) / previousRating) * 100
      return variation > 0 ? `+${variation.toFixed(1)}%` : `${variation.toFixed(1)}%`
    },
    
    getMonthlyVariationClass() {
      const variation = this.getMonthlyVariation()
      if (variation === 'N/A') return 'neutral'
      
      return variation.startsWith('+') ? 'positive' : variation === '0.0%' ? 'neutral' : 'negative'
    },
    
    getMonthlyVariationIcon() {
      const variationClass = this.getMonthlyVariationClass()
      
      if (variationClass === 'positive') return 'fa-arrow-up'
      if (variationClass === 'negative') return 'fa-arrow-down'
      return 'fa-minus'
    },
    
    getStatusText() {
      const rating = parseFloat(this.getCurrentRating())
      
      if (rating >= 4.5) return 'Excelente'
      if (rating >= 4.0) return 'Ótimo'
      if (rating >= 3.5) return 'Bom'
      if (rating >= 3.0) return 'Regular'
      if (rating >= 2.0) return 'Atenção'
      return 'Crítico'
    },
    
    getStatusClass() {
      const status = this.getStatusText()
      
      if (['Excelente', 'Ótimo'].includes(status)) return 'positive'
      if (['Bom', 'Regular'].includes(status)) return 'neutral'
      return 'negative'
    },
    
    getStatusIcon() {
      const status = this.getStatusText()
      
      if (['Excelente', 'Ótimo'].includes(status)) return 'fa-check-circle'
      if (['Bom', 'Regular'].includes(status)) return 'fa-exclamation-circle'
      return 'fa-times-circle'
    },
    
    getEstablishmentsCount() {
      if (!this.categoryData || !this.categoryData.establishments) {
        return 0
      }
      
      return this.categoryData.establishments.length
    },
    
    getSituationalSummary() {
      const rating = parseFloat(this.getCurrentRating())
      const variation = this.getMonthlyVariation()
      const variationClass = this.getMonthlyVariationClass()
      const status = this.getStatusText()
      const count = this.getEstablishmentsCount()
      
      if (variationClass === 'positive') {
        return `O setor de ${this.categoryName} apresenta uma tendência positiva em ${this.monthName}/${this.year}, com avaliação de ${rating.toFixed(1)}/5 e variação de ${variation} em relação ao mês anterior. Com ${count} estabelecimentos monitorados, o setor está classificado como "${status}", indicando uma boa percepção pelos visitantes. Recomenda-se continuar monitorando e implementar as ações sugeridas neste plano para manter o crescimento.`
      } else if (variationClass === 'negative') {
        return `O setor de ${this.categoryName} apresenta uma tendência de queda em ${this.monthName}/${this.year}, com avaliação de ${rating.toFixed(1)}/5 e variação de ${variation} em relação ao mês anterior. Com ${count} estabelecimentos monitorados, o setor está classificado como "${status}". Recomenda-se atenção especial às áreas críticas destacadas neste plano para reverter essa tendência.`
      } else {
        return `O setor de ${this.categoryName} apresenta estabilidade em ${this.monthName}/${this.year}, com avaliação de ${rating.toFixed(1)}/5 e variação estável em relação ao mês anterior. Com ${count} estabelecimentos monitorados, o setor está classificado como "${status}". As recomendações deste plano podem ajudar a fortalecer os pontos positivos e melhorar áreas específicas.`
      }
    }
  }
}
</script>

<style scoped>
.overview-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 20px;
  color: #0f172a;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 25px;
}

.metric-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.metric-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.current-rating .metric-icon {
  background: linear-gradient(135deg, #324DDD, #6c63ff);
  color: white;
}

.monthly-variation .metric-icon {
  background: #f8fafc;
}

.positive .metric-icon {
  background: #dcfce7;
  color: #16a34a;
}

.negative .metric-icon {
  background: #fee2e2;
  color: #dc2626;
}

.neutral .metric-icon {
  background: #f1f5f9;
  color: #64748b;
}

.establishments .metric-icon {
  background: #f0f9ff;
  color: #0284c7;
}

.metric-info {
  flex: 1;
}

.metric-label {
  display: block;
  font-size: 14px;
  color: #64748b;
  margin-bottom: 5px;
}

.metric-value {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.positive .metric-value {
  color: #16a34a;
}

.negative .metric-value {
  color: #dc2626;
}

.neutral .metric-value {
  color: #64748b;
}

.out-of {
  font-size: 16px;
  opacity: 0.8;
}

.summary-box {
  background: linear-gradient(to right, #f8fafc, #f1f5f9);
  border-left: 4px solid #324DDD;
  padding: 20px 25px;
  border-radius: 6px;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-title i {
  color: #324DDD;
}

.summary-text {
  font-size: 15px;
  line-height: 1.6;
  color: #334155;
  margin: 0;
}

@media (max-width: 1200px) {
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style> 